<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.entities.vehicle.model', 2), to: '/settings/vehicles/models' }
    ]"
  >
    <template #right>
      <Button
        :label="$t('ui.crud.new', { item: $t('ui.entities.vehicle.model') })"
        severity="primary"
        icon="plus"
        @click="newModel"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Vehicle Models"
      :value="models"
      :loading="isLoading"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-vehicles-models"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'make', 'range', 'model', 'classification', 'bodyStyle']"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between items-center gap-4">
          <div class="flex gap-4 items-center">
            <Input
              v-model="filters['global'].value"
              :placeholder="$t('ui.common.search', 1)"
              icon="search"
            />
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </div>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.id }}</div>
        </template>
      </Column>

      <Column
        field="make"
        header="Make"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div>{{ data.make }}</div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="makeFilterOptions"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-64"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="range"
        header="Range"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.range }}</div>
        </template>
      </Column>

      <Column
        field="model"
        header="Model"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.model }}</div>
        </template>
      </Column>

      <Column
        field="classification"
        header="Classification"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.classification }}</div>
        </template>
      </Column>

      <Column
        field="bodyStyle"
        header="Body Style"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.bodyStyle }}</div>
        </template>
      </Column>

      <Column
        field="isCommercialVehicle"
        header="Commercial Vehicle"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isCommercialVehicle"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Yes
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="secondary"
          >
            No
          </Chip>
        </template>
      </Column>

      <Column
        field="isInProduction"
        header="In Production"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isInProduction"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Yes
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="danger"
          >
            No
          </Chip>
        </template>
      </Column>

      <Column
        field="image"
        header="Image"
      >
        <template #body="{ data }">
          <Image
            v-if="data.image"
            :src="data.image"
            image-class="h-8 max-w-16"
          />
        </template>
      </Column>

      <Column :sortable="false">
        <template #body="{ data }">
          <div>
            <Button
              v-tippy="$t('ui.actions.edit')"
              icon="pencil"
              plain
              severity="secondary"
              @click="editModel(data.id)"
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="editModelModalIsOpen"
      title="Edit Model"
      icon="car"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="editModelHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Save"
      :show-loader-on-confirm="true"
      :is-confirming="editModelApiRequest.isLoading.value"
      :disable-close="editModelApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="((editModelModalIsOpen = false), (editModelHasUnsavedChanges = false))"
      @ok-modal="updateModel()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          label="Make"
          :is-required="v$.make.required?.$invalid"
        >
          <Select
            v-model="selectedModelState.make"
            :options="makeOptions"
            mode="single"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
            :readonly="true"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$.make.$error">
            {{ v$.make.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          label="Range"
          :is-required="v$.range.required?.$invalid"
        >
          <Input
            v-model="selectedModelState.range"
            :has-error="v$.range.$error"
          />
          <InputError :has-error="v$.range.$error">{{ v$.range.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          label="Model"
          :is-required="v$.model.required?.$invalid"
        >
          <Input
            v-model="selectedModelState.model"
            :has-error="v$.model.$error"
          />
          <InputError :has-error="v$.model.$error">{{ v$.model.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          label="Classification"
          :is-required="v$.classification.required?.$invalid"
        >
          <Select
            v-model="selectedModelState.classification"
            :options="classificationOptions"
            mode="single"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$.classification.$error">
            {{ v$.classification.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          label="Body Style"
          :is-required="v$.bodyStyle.required?.$invalid"
        >
          <Select
            v-model="selectedModelState.bodyStyle"
            :options="bodyStyleOptions"
            mode="single"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$.bodyStyle.$error">{{ v$.bodyStyle.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          label="Image"
          :is-required="false"
        >
          <Input
            v-model="selectedModelState.image"
            :has-error="v$.image.$error"
          />
          <InputError :has-error="v$.image.$error">
            {{ v$.image.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          label="Commercial Vehicle"
          :is-required="false"
        >
          <ToggleSwitch v-model="selectedModelState.isCommercialVehicle" />
        </FormGroup>
        <FormGroup
          label="In Production"
          :is-required="false"
        >
          <ToggleSwitch v-model="selectedModelState.isInProduction" />
        </FormGroup>
      </div>
    </Modal>

    <Modal
      :is-open="newModelModalIsOpen"
      title="New Model"
      icon="car"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="newModelHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Submit"
      :show-loader-on-confirm="true"
      :is-confirming="newModelApiRequest.isLoading.value"
      :disable-close="newModelApiRequest.isLoading.value"
      min-width="600"
      overflow-behaviour="visible"
      @close-modal="((newModelModalIsOpen = false), (newModelHasUnsavedChanges = false))"
      @ok-modal="submitNewModel()"
    >
      <div class="grid grid-cols-2 gap-x-6">
        <FormGroup
          label="Make"
          :is-required="v$new.make.required?.$invalid"
        >
          <Select
            v-model="newModelState.make"
            :options="makeOptions"
            mode="single"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$new.make.$error">
            {{ v$new.make.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          label="Range"
          :is-required="v$new.range.required?.$invalid"
        >
          <Input
            v-model="newModelState.range"
            :has-error="v$new.range.$error"
          />
          <InputError :has-error="v$new.range.$error">{{ v$new.range.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          label="Model"
          :is-required="v$new.model.required?.$invalid"
        >
          <Input
            v-model="newModelState.model"
            :has-error="v$new.model.$error"
          />
          <InputError :has-error="v$new.model.$error">{{ v$new.model.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          label="Classification"
          :is-required="v$new.classification.required?.$invalid"
        >
          <Select
            v-model="newModelState.classification"
            :options="classificationOptions"
            mode="single"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$new.classification.$error">
            {{ v$new.classification.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          label="Body Style"
          :is-required="v$new.bodyStyle.required?.$invalid"
        >
          <Select
            v-model="newModelState.bodyStyle"
            :options="bodyStyleOptions"
            mode="single"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Select"
            class="w-full"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
          <InputError :has-error="v$new.bodyStyle.$error">
            {{ v$new.bodyStyle.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          label="Image"
          :is-required="false"
        >
          <Input
            v-model="newModelState.image"
            :has-error="v$new.image.$error"
          />
          <InputError :has-error="v$new.image.$error">
            {{ v$new.image.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          label="Commercial Vehicle"
          :is-required="false"
        >
          <ToggleSwitch v-model="newModelState.isCommercialVehicle" />
        </FormGroup>
        <FormGroup
          label="In Production"
          :is-required="false"
        >
          <ToggleSwitch v-model="newModelState.isInProduction" />
        </FormGroup>
      </div>
    </Modal>
  </teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

// components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Modal from '@/components/modal/Modal.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import Chip from '@/components/chip/Chip.vue'
import Image from '@/components/image/Image.vue'
import Select from '@/components/forms/Select.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import { alertToast } from '@/utilities/notification'

// composables
import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const models = ref(null)
const modelsApiRequest = useApiRequest()
const editModelApiRequest = useApiRequest()
const newModelApiRequest = useApiRequest()
const isLoading = modelsApiRequest.isLoading

const editModelModalIsOpen = ref(false)
const editModelHasUnsavedChanges = ref(false)
const selectedModelInitState = ref(null)
const selectedModelState = ref(null)

const newModelModalIsOpen = ref(false)
const newModelHasUnsavedChanges = ref(false)
const newModelInitState = ref(null)
const newModelState = ref(null)
const filters = ref([])

const manufacturers = useApiRequest()
const manufacturersList = ref([])

// Classification options
const classificationOptions = [
  { value: 'car', label: 'Car' },
  { value: 'van', label: 'Van' },
  { value: 'truck', label: 'Truck' },
  { value: 'motorcycle', label: 'Motorcycle' }
]

// Body style options in alphabetical order
const bodyStyleOptions = [
  { value: 'Campervan', label: 'Campervan' },
  { value: 'Coupe', label: 'Coupe' },
  { value: 'Estate', label: 'Estate' },
  { value: 'Hatchback', label: 'Hatchback' },
  { value: 'MPV', label: 'MPV' },
  { value: 'Saloon', label: 'Saloon' },
  { value: 'SUV', label: 'SUV' },
  { value: 'Van', label: 'Van' }
]

/**
 * Computed property for make options from manufacturers API
 */
const makeOptions = computed(() => {
  if (!manufacturersList.value || manufacturersList.value.length === 0) {
    return []
  }
  return manufacturersList.value.map(manufacturer => ({
    value: manufacturer.name,
    label: manufacturer.name
  }))
})

/**
 * Fetch manufacturers list for make dropdown
 */
try {
  manufacturers.send({ endpoint: '/v1/manufacturers' }).then(response => {
    if (response.success) {
      manufacturersList.value = response.data
    } else {
      alertToast('Failed to fetch manufacturers', response.message, 'error')
    }
  })
} catch (err) {
  alertToast('Failed to fetch manufacturers', err.message, 'error')
}

/**
 * Computed property for make filter options - extracts unique makes from models
 */
const makeFilterOptions = computed(() => {
  if (!models.value || !Array.isArray(models.value)) {
    return []
  }
  
  // Get unique makes from the models
  const uniqueMakes = [...new Set(models.value.map(model => model.make).filter(Boolean))]
  
  // Sort alphabetically and convert to options format
  return uniqueMakes
    .sort()
    .map(make => ({
      value: make,
      label: make
    }))
})


/**
 * Initialize filters
 */
function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.IN },
    make: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

/**
 * Clear all filters
 */
function clearFilters() {
  sessionStorage.removeItem('dt-vehicles-models')
  initFilters()
}

/**
 * Fetch models from API
 */
function fetchModels() {
  modelsApiRequest
    .send({
      endpoint: '/v1/vehicles/models'
    })
    .then(response => {
      if (response.success) {
        // API returns an array of objects
        models.value = Array.isArray(response.data) ? response.data : []
      } else {
        alertToast('Error', 'Failed to fetch models', 'error')
      }
    })
}

// Fetch models on component mount
fetchModels()

/**
 * Edit model settings
 * @param {number} id - The model ID
 */
function editModel(id) {
  selectedModelInitState.value = models.value.find(model => model.id === id)
  selectedModelState.value = { ...selectedModelInitState.value }
  if (selectedModelState.value) {
    editModelModalIsOpen.value = true
  } else {
    alertToast('Error', 'Could not find model', 'error')
  }
}

/**
 * Create new model
 */
function newModel() {
  newModelInitState.value = {
    make: null,
    range: null,
    model: null,
    classification: null,
    bodyStyle: null,
    isCommercialVehicle: false,
    isInProduction: true,
    image: null
  }

  newModelState.value = { ...newModelInitState.value }
  newModelModalIsOpen.value = true
}

const InputRules = {
  id: {},
  make: { required },
  range: { required },
  model: { required },
  classification: { required },
  bodyStyle: { required },
  isCommercialVehicle: {},
  isInProduction: {},
  image: {}
}

const v$ = useVuelidate(InputRules, selectedModelState, { $stopPropagation: true })
const v$new = useVuelidate(InputRules, newModelState)

/**
 * Update model settings
 */
async function updateModel() {
  const isFormCorrect = await v$.value.$validate()

  if (!isFormCorrect) {
    return
  }

  const dataToPost = { ...selectedModelState.value }
  try {
    editModelApiRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/vehicles/models/${dataToPost.id}`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (!response.success) {
          alertToast('Error', 'Failed to update model', 'error')
        } else {
          alertToast('Success!', 'Model details updated', 'success')
          editModelModalIsOpen.value = false
          editModelHasUnsavedChanges.value = false
          fetchModels()
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$.value.$reset()
}

/**
 * Submit new model
 */
async function submitNewModel() {
  const isFormCorrect = await v$new.value.$validate()
  if (!isFormCorrect) {
    return
  }

  const dataToPost = { ...newModelState.value }
  try {
    newModelApiRequest
      .send({
        method: 'POST',
        endpoint: '/v1/vehicles/models',
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (!response.success) {
          alertToast('Error', 'Failed to add model', 'error')
        } else {
          alertToast('Success!', 'Model added', 'success')
          newModelModalIsOpen.value = false
          newModelHasUnsavedChanges.value = false
          fetchModels()
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$new.value.$reset()
}

watch(
  selectedModelState,
  newValue => {
    editModelHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(selectedModelInitState.value)
  },
  { deep: true }
)

watch(
  newModelState,
  newValue => {
    newModelHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(newModelInitState.value)
  },
  { deep: true }
)
</script>

<style></style>
